<template>
    <b-modal :id="modalId" hide-footer no-close-on-backdrop size="xl" title="Fax Preview" @hidden="onHidden">
        <div :id="containerId" class="fax-pdf-preview">
            <div v-if="isLoading" class="text-center my-2">
                <div>
                    <b-spinner class="align-middle"></b-spinner>
                </div>
                <strong>Loading...</strong>
            </div>
        </div>
    </b-modal>
</template>

<script lang="ts">
import { Component, Prop, Vue } from "vue-property-decorator";
import PDFObject from "pdfobject";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import { readBlobError } from "@/util/readBlobError";

/**
 * Reusable modal that renders a fax preview PDF returned by a `/preview` endpoint.
 * Callers invoke `open(loader)` with a function that resolves to the PDF blob; the modal
 * embeds it and revokes the created object URL when the modal closes.
 */
@Component({ name: "FaxPreviewModal" })
export default class FaxPreviewModal extends Vue {
    @Prop({ default: "fax-preview" }) readonly modalId!: string;

    isLoading = false;
    private objectUrl: string | null = null;

    get containerId(): string {
        return `${this.modalId}-container`;
    }

    /** Opens the modal and embeds the PDF produced by the supplied loader. */
    async open(loader: () => Promise<Blob>): Promise<void> {
        this.revoke();
        this.$bvModal.show(this.modalId);
        this.isLoading = true;
        try {
            const blob = await loader();
            const url = URL.createObjectURL(blob);
            this.objectUrl = url;
            this.$nextTick(() => {
                PDFObject.embed(url, `#${this.containerId}`, {
                    pdfOpenParams: { view: "FitW", pagemode: "thumbs", toolbar: 1, scrollbar: 0 },
                });
                this.isLoading = false;
            });
        } catch (err: any) {
            this.isLoading = false;
            this.$bvModal.hide(this.modalId);
            this.$notification(NotificationOptions.error(await readBlobError(err)));
        }
    }

    onHidden(): void {
        this.revoke();
    }

    private revoke(): void {
        if (this.objectUrl) {
            URL.revokeObjectURL(this.objectUrl);
            this.objectUrl = null;
        }
    }
}
</script>

<style scoped>
.fax-pdf-preview {
    height: calc(100vh - 220px);
}
</style>
